import {computed, decorate, observable} from 'mobx';
import {isEmpty} from 'lodash/lang';
import {faUser} from '@fortawesome/pro-solid-svg-icons';
import {BaseModel} from './base';
import {Scope, superUser} from './scope';

export const GUEST_USER_ID = 0;
export const SYSTEM_USER_ID = 1;
export const ADMINISTRATOR_USER_ID = 2;

export class User extends BaseModel {
  static faIcon = faUser;

  scopes = [];

  saveableProperties = ['password', 'username', 'firstName', 'lastName', 'email', 'properties'];

  static prepareApiPayload(model) {
    return {
      id: model.id || undefined,
      password: model.password || undefined,
      username: model.username || undefined,
      firstName: model.firstName || null,
      lastName: model.lastName || null,
      email: model.email || null,
      properties: model.properties || null,
    };
  }

  populateAttributesFromStore(rootStore) {
    super.populateAttributesFromStore(rootStore);
    this.userRoles = rootStore.userRoleStore.userRoles.filter((userRole) => userRole.userId === this.id);
  }

  setScopes(scopes) {
    this.scopes = scopes.map((scope) => new Scope(scope));
  }

  hasRole(roleId) {
    return this.roleIds.includes(roleId);
  }

  searchableProperties = ['username', 'firstName', 'lastName'];

  get sortedRoles() {
    return this.userRoles.slice().sort((a, b) => {
      const nameA = a.role?.name.toUpperCase();
      const nameB = b.role?.name.toUpperCase();

      if (nameA < nameB) {
        return -1;
      }

      if (nameA > nameB) {
        return 1;
      }

      return 0;
    });
  }

  get fullName() {
    const firstName = this.firstName.trim();
    const lastName = this.lastName.trim();

    // return username as fallback if name is empty, which should not happen in production because names are required
    return (isEmpty(firstName) && isEmpty(lastName)) ? this.username : `${firstName} ${lastName}`;
  }

  /**
   * Indicates if the logged in user has superUser permissions
   * @return {boolean}
   */
  get isSuperUser() {
    return this.scopes.find((s) => s === superUser);
  }

  /**
   * Indicates if the user has a role with superUser permissions. Requires UserRole and Role dependencies and
   * is slower than isSuperUser, but does work with every user. Might return false at first if data is still loading.
   * @return {boolean}
   */
  get hasSuperUserRole() {
    return !!this.userRoles.find((ur) => ur.role?.isSuperUserRole);
  }
}

decorate(User, {
  id: observable,
  username: observable,
  firstName: observable,
  lastName: observable,
  email: observable,
  properties: observable,
  userRoles: observable,
  createdAt: observable,
  createdBy: observable,
  updatedAt: observable,
  updatedBy: observable,
  scopes: observable,
  sortedRoles: computed,
  fullName: computed,
  isLdapUser: observable,
});
